import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, FileText, Sparkles, User } from "lucide-react";
import { toast } from "react-toastify";
import conferenceAndTradeMissionService from "../../../api/ConferenceAndTradeMission";
import {
  validateGhanaPhoneNumber,
  validateSignature,
  formatGhanaPhoneNumber,
} from "../../../utils/formValidators";
import RegistrationStepper from "../../../components/event_media/trade_mission_registration/RegistrationStepper";
import Step1CompanyInfo from "../../../components/event_media/trade_mission_registration/Step1CompanyInfo";
import Step2ContactDetails from "../../../components/event_media/trade_mission_registration/Step2ContactDetails";
import Step3DescriptionServices from "../../../components/event_media/trade_mission_registration/Step3DescriptionServices";
import Step4AdditionalInfo from "../../../components/event_media/trade_mission_registration/Step4AdditionalInfo";
import Step5Declaration from "../../../components/event_media/trade_mission_registration/Step5Declaration";
import Step6Preview from "../../../components/event_media/trade_mission_registration/Step6Preview";

const TOTAL_STEPS = 6;

function getClientDisplayName(selectedClient) {
  const client = selectedClient?.client || selectedClient;
  const full =
    client?.fullName ??
    ([client?.firstName ?? client?.first_name, client?.lastName ?? client?.last_name]
      .filter(Boolean)
      .join(" "));
  return full || client?.email || "Client";
}

function getInitialFormData(selectedClient) {
  const client = selectedClient?.client || selectedClient;
  const name = getClientDisplayName(selectedClient);
  const phone = client?.phone ?? client?.phone_number ?? "";
  const email = client?.email ?? "";
  const today = new Date();
  const declarationDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  return {
    companyName: client?.organization ?? "",
    telephoneNumber: phone,
    faxNumber: "",
    websiteAddress: "",
    vatRegistrationNumber: "",
    cellNumber: phone,
    businessEmail: email,
    contactPersonName: name,
    contactPersonDesignation: "",
    contactPersonTelephone: phone,
    contactPersonCellNumber: phone,
    contactPersonEmail: email,
    secondContactPersonName: "",
    secondContactPersonDesignation: "",
    secondContactPersonTelephone: "",
    secondContactPersonCellNumber: "",
    secondContactPersonEmail: "",
    companyDescription: "",
    selectedServices: [],
    otherServiceSpecification: "",
    selectedCategories: [],
    otherCategorySpecification: "",
    applicantName: name,
    signatoryDesignation: "Authorized Representative",
    applicantSignature: "",
    declarationDate,
  };
}

export default function AgentTradeMissionRegistration({ eventData, slug, selectedClient, onBack }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState(() => getInitialFormData(selectedClient));
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const client = selectedClient?.client || selectedClient;
  const clientName = getClientDisplayName(selectedClient);

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 0) {
      if (!formData.companyName?.trim()) newErrors.companyName = "Company name is required";
      const telephoneValidation = validateGhanaPhoneNumber(formData.telephoneNumber?.replace(/\s/g, ""));
      if (!formData.telephoneNumber?.trim()) newErrors.telephoneNumber = "Telephone number is required";
      else if (!telephoneValidation.isValid) newErrors.telephoneNumber = telephoneValidation.message;
      const cellValidation = validateGhanaPhoneNumber(formData.cellNumber?.replace(/\s/g, ""));
      if (!formData.cellNumber?.trim()) newErrors.cellNumber = "Cell number is required";
      else if (!cellValidation.isValid) newErrors.cellNumber = cellValidation.message;
      if (!formData.vatRegistrationNumber?.trim()) newErrors.vatRegistrationNumber = "VAT registration number is required";
      if (!formData.businessEmail?.trim()) newErrors.businessEmail = "Business email is required";
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.businessEmail)) newErrors.businessEmail = "Please enter a valid email address";
    }

    if (step === 1) {
      if (!formData.contactPersonName?.trim()) newErrors.contactPersonName = "Contact person name is required";
      if (!formData.contactPersonDesignation?.trim()) newErrors.contactPersonDesignation = "Designation is required";
      const contactTel = validateGhanaPhoneNumber(formData.contactPersonTelephone?.replace(/\s/g, ""));
      if (!formData.contactPersonTelephone?.trim()) newErrors.contactPersonTelephone = "Telephone number is required";
      else if (!contactTel.isValid) newErrors.contactPersonTelephone = contactTel.message;
      const contactCell = validateGhanaPhoneNumber(formData.contactPersonCellNumber?.replace(/\s/g, ""));
      if (!formData.contactPersonCellNumber?.trim()) newErrors.contactPersonCellNumber = "Cell number is required";
      else if (!contactCell.isValid) newErrors.contactPersonCellNumber = contactCell.message;
      if (!formData.contactPersonEmail?.trim()) newErrors.contactPersonEmail = "Email address is required";
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactPersonEmail)) newErrors.contactPersonEmail = "Please enter a valid email address";
      if (!formData.secondContactPersonName?.trim()) newErrors.secondContactPersonName = "Second contact person name is required";
      if (!formData.secondContactPersonDesignation?.trim()) newErrors.secondContactPersonDesignation = "Designation is required";
      const secondTel = validateGhanaPhoneNumber(formData.secondContactPersonTelephone?.replace(/\s/g, ""));
      if (!formData.secondContactPersonTelephone?.trim()) newErrors.secondContactPersonTelephone = "Telephone number is required";
      else if (!secondTel.isValid) newErrors.secondContactPersonTelephone = secondTel.message;
      const secondCell = validateGhanaPhoneNumber(formData.secondContactPersonCellNumber?.replace(/\s/g, ""));
      if (!formData.secondContactPersonCellNumber?.trim()) newErrors.secondContactPersonCellNumber = "Cell number is required";
      else if (!secondCell.isValid) newErrors.secondContactPersonCellNumber = secondCell.message;
      if (!formData.secondContactPersonEmail?.trim()) newErrors.secondContactPersonEmail = "Email address is required";
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.secondContactPersonEmail)) newErrors.secondContactPersonEmail = "Please enter a valid email address";
    }

    if (step === 2) {
      if (!formData.companyDescription?.trim()) newErrors.companyDescription = "Company description is required";
      else if (formData.companyDescription.trim().length < 50) newErrors.companyDescription = "Please provide a more detailed description (at least 50 characters)";
      if (!formData.selectedServices?.length) newErrors.selectedServices = "Please select at least one service of interest";
      if (formData.selectedServices?.includes("other") && !formData.otherServiceSpecification?.trim()) newErrors.otherServiceSpecification = "Please specify the other service";
    }

    if (step === 3) {
      if (!formData.selectedCategories?.length) newErrors.selectedCategories = "Please select at least one category";
      if (formData.selectedCategories?.includes("other") && !formData.otherCategorySpecification?.trim()) newErrors.otherCategorySpecification = "Please specify the other category";
    }

    if (step === 4) {
      if (!formData.applicantName?.trim()) newErrors.applicantName = "Applicant name is required";
      if (!formData.signatoryDesignation?.trim()) newErrors.signatoryDesignation = "Signatory designation is required";
      const sigValidation = validateSignature(formData.applicantSignature, formData.applicantName);
      if (!formData.applicantSignature?.trim()) newErrors.applicantSignature = "Applicant signature (initials) is required";
      else if (!sigValidation.isValid) newErrors.applicantSignature = sigValidation.message;
      if (!formData.declarationDate?.trim()) newErrors.declarationDate = "Declaration date is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep === TOTAL_STEPS - 1) {
        handleSubmit();
      } else {
        setCurrentStep(currentStep + 1);
      }
    } else {
      toast.error("Please fill in all required fields");
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      setErrors({});
    }
  };

  const transformFormDataToPayload = () => {
    const payload = {
      event_type: "trade_mission",
      event_slug: slug,
      company_name: formData.companyName,
      telephone_number: formatGhanaPhoneNumber(formData.telephoneNumber?.replace(/\s/g, "")),
      cell_number: formatGhanaPhoneNumber(formData.cellNumber?.replace(/\s/g, "")),
      business_email: formData.businessEmail,
      vat_registration_number: formData.vatRegistrationNumber,
      contact_person_name: formData.contactPersonName,
      contact_person_designation: formData.contactPersonDesignation,
      contact_person_telephone: formatGhanaPhoneNumber(formData.contactPersonTelephone?.replace(/\s/g, "")),
      contact_person_cell_number: formatGhanaPhoneNumber(formData.contactPersonCellNumber?.replace(/\s/g, "")),
      contact_person_email: formData.contactPersonEmail,
      second_contact_person_name: formData.secondContactPersonName,
      second_contact_person_designation: formData.secondContactPersonDesignation,
      second_contact_person_telephone: formatGhanaPhoneNumber(formData.secondContactPersonTelephone?.replace(/\s/g, "")),
      second_contact_person_cell_number: formatGhanaPhoneNumber(formData.secondContactPersonCellNumber?.replace(/\s/g, "")),
      second_contact_person_email: formData.secondContactPersonEmail,
      company_description: formData.companyDescription,
      selected_services: formData.selectedServices,
      selected_categories: formData.selectedCategories,
      applicant_name: formData.applicantName,
      signatory_designation: formData.signatoryDesignation,
      applicant_signature: formData.applicantSignature,
      declaration_date: formData.declarationDate,
      client_slug: client?.slug ?? client?.id ?? "",
      client_type: selectedClient?.clientType ?? "clients",
      client_data: {
        client_slug: client?.slug ?? client?.id ?? "",
        client_type: selectedClient?.clientType ?? "clients",
      },
    };
    if (formData.faxNumber) payload.fax_number = formData.faxNumber;
    if (formData.websiteAddress) payload.website_address = formData.websiteAddress;
    payload.other_service_specification = formData.selectedServices?.includes("other") ? (formData.otherServiceSpecification?.trim() || "") : "";
    payload.other_category_specification = formData.selectedCategories?.includes("other") ? (formData.otherCategorySpecification?.trim() || "") : "";
    return payload;
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const payload = transformFormDataToPayload();
      const response = await conferenceAndTradeMissionService.createAgentEvent(payload);
      const ok = response.status === true || response.data?.status_code === 200;
      if (ok) {
        toast.success(response.message || "Trade mission registration submitted successfully!");
        if (onBack) onBack();
      } else {
        toast.error(response.message || "Failed to submit registration. Please try again.");
      }
    } catch (err) {
      toast.error(err?.message || "An unexpected error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (onBack) onBack();
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return <Step1CompanyInfo formData={formData} setFormData={setFormData} errors={errors} />;
      case 1:
        return <Step2ContactDetails formData={formData} setFormData={setFormData} errors={errors} />;
      case 2:
        return <Step3DescriptionServices formData={formData} setFormData={setFormData} errors={errors} />;
      case 3:
        return <Step4AdditionalInfo formData={formData} setFormData={setFormData} errors={errors} />;
      case 4:
        return (
          <Step5Declaration
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            clientName={clientName}
            isAgentRegistration
          />
        );
      case 5:
        return <Step6Preview formData={formData} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 via-white to-gray-50">
      <div className="overflow-hidden sticky top-16 z-40 bg-gradient-to-br from-purple-500 via-purple-600 to-purple-700 shadow-lg lg:top-20">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 2px 2px, white 1px, transparent 0)", backgroundSize: "40px 40px" }} />
        <div className="relative px-4 py-6 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center">
              <motion.button
                type="button"
                onClick={handleClose}
                whileHover={{ x: -4, scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex gap-2 items-center self-start px-5 py-2.5 text-sm font-semibold text-white bg-white/20 backdrop-blur-sm rounded-xl border border-white/30"
              >
                <ChevronLeft size={18} className="text-white" />
                Back to Event
              </motion.button>
              <div className="flex flex-col gap-2 items-center text-center sm:flex-1">
                <div className="flex gap-2 items-center">
                  <div className="flex justify-center items-center w-10 h-10 text-purple-600 bg-white rounded-xl shadow-lg">
                    <FileText size={20} />
                  </div>
                  <h1 className="text-2xl font-bold text-white sm:text-3xl">Trade Mission Registration</h1>
                </div>
                <p className="text-sm text-purple-100 sm:text-base">Registering on behalf of your client</p>
                <div className="flex gap-2 items-center mt-1 px-3 py-1.5 bg-white/20 rounded-lg">
                  <User size={16} className="text-white" />
                  <span className="text-sm font-medium text-white">{clientName}</span>
                </div>
              </div>
              <div className="hidden justify-center items-center w-12 h-12 sm:flex text-white/20">
                <Sparkles size={24} />
              </div>
            </div>
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="overflow-hidden mt-6 h-1 rounded-full bg-white/20"
            >
              <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: (currentStep + 1) / TOTAL_STEPS }}
                transition={{ duration: 0.4, delay: 0.3 }}
                className="h-full bg-white rounded-full shadow-lg"
                style={{ transformOrigin: "left" }}
              />
            </motion.div>
          </div>
        </div>
      </div>

      <div className="px-4 py-8 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="mb-8">
            <RegistrationStepper currentStep={currentStep} />
          </div>
          <AnimatePresence mode="wait">{renderStep()}</AnimatePresence>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            className="sticky bottom-0 px-8 py-6 mt-8 bg-white rounded-xl border border-gray-200 shadow-lg"
          >
            <div className="flex justify-between items-center">
              <motion.button
                type="button"
                onClick={handleBack}
                disabled={currentStep === 0}
                whileHover={currentStep > 0 ? { scale: 1.02 } : {}}
                whileTap={currentStep > 0 ? { scale: 0.98 } : {}}
                className={
                  currentStep === 0
                    ? "px-6 py-3 text-sm font-semibold rounded-lg text-gray-400 bg-gray-100 cursor-not-allowed"
                    : "px-6 py-3 text-sm font-semibold rounded-lg text-gray-700 bg-gray-100 hover:bg-gray-200"
                }
              >
                Back
              </motion.button>
              <motion.button
                type="button"
                onClick={handleNext}
                disabled={isSubmitting}
                whileHover={!isSubmitting ? { scale: 1.02 } : {}}
                whileTap={!isSubmitting ? { scale: 0.98 } : {}}
                className="px-8 py-3 text-base font-bold text-white bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg shadow-md hover:from-purple-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <span className="flex gap-2 items-center">
                    <span className="w-5 h-5 rounded-full border-2 border-white animate-spin border-t-transparent" />
                    Submitting...
                  </span>
                ) : currentStep === TOTAL_STEPS - 1 ? (
                  "Submit"
                ) : (
                  "Continue"
                )}
              </motion.button>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
